import * as React from 'react';

import { type HeroBentoCardType, HeroBentoModule } from '@coursera-web/cds-labs';
import { useLayer } from '@coursera-web/statsig';

import CampaignHeroModuleRenderer, {
  useShouldShowCampaignHeroModule,
} from 'bundles/campaigns/module-renderers/CampaignHeroModuleRenderer';
import EnrollOrUpgradeToCourseraPlus from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/EnrollOrUpgradeToCourseraPlus';
import { isCourseraPlusCheckoutFlowCtaTarget } from 'bundles/coursera-plus-common/utils/courseraPlusCollectionsUtils';
import type { HeroBentoModuleFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import SeoPageHeader from 'bundles/front-page/components/modules/hero-banner/SeoPageHeader';
import getPredefinedPromos from 'bundles/front-page/redesign-modules/hero-banner/data/getPredefinedPromos';

type Props = {
  content?: HeroBentoModuleFragment;
};

const HeroBannerModuleRenderer = ({ content }: Props) => {
  const layer = useLayer('merchandising-fe-aa-test-layer');
  // Statsig exposure logged here
  layer.get('variant', 'a');
  const shouldShowCampaignHeroModule = useShouldShowCampaignHeroModule();

  let heroModuleOutput;

  if (shouldShowCampaignHeroModule) {
    heroModuleOutput = <CampaignHeroModuleRenderer />;
  } else {
    let cards: HeroBentoCardType[];
    if (content) {
      cards = (content.cardsCollection?.items?.filter(Boolean) ?? [])?.map((item, index) => {
        const isCPlusCheckoutFlow = isCourseraPlusCheckoutFlowCtaTarget(item.ctaTarget);

        return {
          id: item.sys.id,
          index,
          ...item,
          eyebrow: {
            image: item.eyebrowImage,
            eyebrowImageHeight: item.eyebrowImageHeight,
          },
          cta: {
            target: item.ctaTarget,
            label: item.ctaText,
          },
          ...(isCPlusCheckoutFlow && {
            customPrimaryCta: (
              <EnrollOrUpgradeToCourseraPlus
                ctaLabel={item.ctaText}
                ctaVariant="primary"
                trackingName="hero_bento_primary_cta"
                shouldDisableIndiaExperiment
                ctaTarget={item.ctaTarget}
                shouldUseHookForPostAuthCheckout
                ctaSize="small"
              />
            ),
          }),
          desktopImage: {
            ...item.image,
          },
          mobileImage: {
            ...(item.mobileImage ?? item.image),
          },
        };
      }) as Array<HeroBentoCardType>;
    } else {
      const defaultCards = getPredefinedPromos();
      cards = [defaultCards.joinForFree, defaultCards.c4b, defaultCards.promoCert];
    }
    heroModuleOutput = <HeroBentoModule cards={cards} />;
  }
  return (
    <div data-testid="front-page-hero-banner-module">
      <SeoPageHeader />
      {heroModuleOutput}
    </div>
  );
};

export default HeroBannerModuleRenderer;
